// Generate case number (client-side preview version)
export function generateCaseNumber(): string {
  const date = new Date()
  const year = date.getFullYear().toString().slice(-2)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')

  return `AJ${year}${month}${day}${random}`
}

// Format phone number (Malaysian or Singaporean format)
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')

  if (cleaned.startsWith('60')) {
    if (cleaned.length === 11) { // Mobile
      return `+60 ${cleaned.slice(2, 4)}-${cleaned.slice(4, 8)} ${cleaned.slice(8)}`
    } else if (cleaned.length === 10) { // Landline
      return `+60 ${cleaned.slice(2, 3)}-${cleaned.slice(3, 7)} ${cleaned.slice(7)}`
    }
  }

  // Singaporean number (+65 XXXX XXXX)
  if (cleaned.startsWith('65') && cleaned.length === 10) {
    return `+65 ${cleaned.slice(2, 6)} ${cleaned.slice(6)}`
  }

  return phone
}

// Calculate age from date of birth
export function calculateAge(dateOfBirth: Date): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)

  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

// Format currency
export function formatCurrency(amount: number, currency: string = 'RM'): string {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
  }).format(amount)
}

// Format date
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  return new Intl.DateTimeFormat('en-MY', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  }).format(dateObj)
}

// Format date and time
export function formatDateTime(date: Date | string): string {
  return formatDate(date, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Format time
export function formatTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  return new Intl.DateTimeFormat('en-MY', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

// Get status color
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    INITIATED: 'bg-blue-100 text-blue-800',
    BOOKING_APPROVED: 'bg-blue-100 text-blue-800',
    PAYMENT_MADE: 'bg-orange-100 text-orange-800',
    PAYMENT_APPROVED: 'bg-green-100 text-green-800',
    DOCUMENTS_PENDING: 'bg-yellow-100 text-yellow-800',
    TRANSPORT_SCHEDULED: 'bg-purple-100 text-purple-800',
    GHUSL_SCHEDULED: 'bg-indigo-100 text-indigo-800',
    KAFAN_SCHEDULED: 'bg-pink-100 text-pink-800',
    JANAZAH_SCHEDULED: 'bg-orange-100 text-orange-800',
    BURIAL_SCHEDULED: 'bg-red-100 text-red-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
    CONFIRMED: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-indigo-100 text-indigo-800',
    PAID: 'bg-green-100 text-green-800',
    UNPAID: 'bg-red-100 text-red-800',
  }

  return statusColors[status] || 'bg-gray-100 text-gray-800'
}